/**
 * SkillRegistry — typed storage for a player's acquired skills and perks.
 *
 * Replaces the ad-hoc (player as any)._skill* / _perk* hack pattern.
 * Provides typed access, iteration, and out-of-combat value queries.
 */

import type { ModifierBag } from "../../../framework/stats";
import { SkillInstance, SkillDef } from "./SkillDef";
import { PerkInstance, PerkDef } from "./PerkDef";

export class SkillRegistry {
  private skills: Map<string, SkillInstance> = new Map();
  private perks: Map<string, PerkInstance> = new Map();

  // --- Skills ---

  /** Add or level up a skill. Returns the instance. */
  addSkill(def: SkillDef): SkillInstance {
    const existing = this.skills.get(def.id);
    if (existing) {
      existing.levelUp();
      return existing;
    }
    const instance = new SkillInstance(def);
    this.skills.set(def.id, instance);
    return instance;
  }

  /** Get a skill instance by ID, or null. */
  getSkill(id: string): SkillInstance | null {
    return this.skills.get(id) ?? null;
  }

  /** Check if the player has a skill. */
  hasSkill(id: string): boolean {
    return this.skills.has(id);
  }

  /** Get the level of a skill (0 if not acquired). */
  skillLevel(id: string): number {
    return this.skills.get(id)?.level ?? 0;
  }

  /** Get an out-of-combat value from a skill. */
  skillValue(id: string, key: string): number {
    return this.skills.get(id)?.getValue(key) ?? 0;
  }

  /** All acquired skills. */
  allSkills(): ReadonlyArray<SkillInstance> {
    return Array.from(this.skills.values());
  }

  // --- Perks ---

  /** Add or stack a perk. Returns the instance. */
  addPerk(def: PerkDef): PerkInstance {
    const existing = this.perks.get(def.id);
    if (existing) {
      existing.addStack();
      return existing;
    }
    const instance = new PerkInstance(def);
    this.perks.set(def.id, instance);
    return instance;
  }

  /** Get a perk instance by ID, or null. */
  getPerk(id: string): PerkInstance | null {
    return this.perks.get(id) ?? null;
  }

  /** Check if the player has a perk. */
  hasPerk(id: string): boolean {
    return this.perks.has(id);
  }

  /** Get stack count of a perk (0 if not acquired). */
  perkStacks(id: string): number {
    return this.perks.get(id)?.stacks ?? 0;
  }

  /** Get an out-of-combat value from a perk. */
  perkValue(id: string, key: string): number {
    return this.perks.get(id)?.getValue(key) ?? 0;
  }

  /** All acquired perks. */
  allPerks(): ReadonlyArray<PerkInstance> {
    return Array.from(this.perks.values());
  }

  // --- Bag wiring ---

  /** Apply all skills and perks to a ModifierBag. Called by BattleBagBuilder. */
  applyAll(bag: ModifierBag): void {
    for (const skill of this.skills.values()) {
      skill.apply(bag);
    }
    for (const perk of this.perks.values()) {
      perk.apply(bag);
    }
  }
}
